/**
 * Lane ordering for the Intake Pipeline board (founder soak, item 3).
 *
 * Two coupled controls, both PER-USER view preferences persisted in
 * localStorage (deliberately the cheap honest tier: the sort mode and the
 * manual order are how ONE intake manager likes their board arranged — not
 * shared team state. If the team later needs a common manual order, the
 * upgrade is a server-persisted rank column; this module's shape survives
 * that swap because the board only consumes `sortLane`):
 *
 *   * per-stage sort MODE — 'created' (newest first; the server's board
 *     order, the default) / 'due' (due date ascending, undated last) /
 *     'manual' (the user's dragged order).
 *   * per-stage MANUAL ORDER — an ordered id list, written when the user
 *     drags a card WITHIN its lane (which also flips that lane to
 *     'manual'). Ids no longer on the board are ignored; new cards not in
 *     the saved order append at the END in incoming (server) order.
 *
 * Pure functions + a storage seam (Pick<Storage,...>) so everything is
 * unit-testable without jsdom localStorage fuss.
 */

import type { WorkItemRow } from '@/lib/api';
import { LANE_SORT_KEY_PREFIX } from '@/lib/keptAcrossSignOut';

export type LaneSortMode = 'created' | 'due' | 'next_action' | 'name' | 'manual' | 'category';

export const LANE_SORT_MODES: readonly LaneSortMode[] = ['created', 'due', 'next_action', 'name', 'manual', 'category'] as const;

export interface LaneSortPrefs {
  /** stage key → the user's sort mode ('created' when absent). */
  modes: Record<string, LaneSortMode>;
  /** stage key → the user's manual card order (work_item ids). */
  manual: Record<string, string[]>;
}

export const EMPTY_LANE_SORT_PREFS: LaneSortPrefs = { modes: {}, manual: {} };

/** Per user + tenant so two staff on one machine never share an order. */
export function laneSortStorageKey(tenantSlug: string, userId: string): string {
  return `${LANE_SORT_KEY_PREFIX}${tenantSlug}.${userId}`;
}

function isMode(v: unknown): v is LaneSortMode {
  return v === 'created' || v === 'due' || v === 'next_action' || v === 'name' || v === 'manual' || v === 'category';
}

/** Defensive parse — a corrupt/legacy blob degrades to the defaults. */
export function loadLaneSortPrefs(
  storage: Pick<Storage, 'getItem'>,
  key: string,
): LaneSortPrefs {
  let raw: string | null = null;
  try {
    raw = storage.getItem(key);
  } catch {
    return { modes: {}, manual: {} };
  }
  if (!raw) return { modes: {}, manual: {} };
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return { modes: {}, manual: {} };
    const p = parsed as Record<string, unknown>;
    const modes: Record<string, LaneSortMode> = {};
    if (typeof p['modes'] === 'object' && p['modes'] !== null) {
      for (const [k, v] of Object.entries(p['modes'] as Record<string, unknown>)) {
        if (isMode(v)) modes[k] = v;
      }
    }
    const manual: Record<string, string[]> = {};
    if (typeof p['manual'] === 'object' && p['manual'] !== null) {
      for (const [k, v] of Object.entries(p['manual'] as Record<string, unknown>)) {
        if (Array.isArray(v) && v.every((x) => typeof x === 'string')) manual[k] = v;
      }
    }
    return { modes, manual };
  } catch {
    return { modes: {}, manual: {} };
  }
}

export function saveLaneSortPrefs(
  storage: Pick<Storage, 'setItem'>,
  key: string,
  prefs: LaneSortPrefs,
): void {
  try {
    storage.setItem(key, JSON.stringify(prefs));
  } catch {
    // Quota/private-mode failures degrade to session-only ordering.
  }
}

/** Sort ONE lane's cards. Never mutates the input. */
export function sortLane(
  items: WorkItemRow[],
  mode: LaneSortMode,
  manualIds: string[] | undefined,
): WorkItemRow[] {
  if (mode === 'due') {
    return [...items].sort((a, b) => {
      if (a.due_date === null && b.due_date === null) return cmpCreatedDesc(a, b);
      if (a.due_date === null) return 1; // undated sink to the bottom
      if (b.due_date === null) return -1;
      const byDue = a.due_date.localeCompare(b.due_date);
      return byDue !== 0 ? byDue : cmpCreatedDesc(a, b);
    });
  }
  if (mode === 'next_action') {
    // The day to touch the card next, soonest first; cards with none planned
    // sink to the bottom; created-desc tiebreak (the 'due' shape).
    return [...items].sort((a, b) => {
      if (a.next_action_date === null && b.next_action_date === null) return cmpCreatedDesc(a, b);
      if (a.next_action_date === null) return 1;
      if (b.next_action_date === null) return -1;
      const byNext = a.next_action_date.localeCompare(b.next_action_date);
      return byNext !== 0 ? byNext : cmpCreatedDesc(a, b);
    });
  }
  if (mode === 'name') {
    // Founder soak (talent board): alphabetical by the card's name, A to Z,
    // case-insensitive, created-desc tiebreak. Uses the same name resolution
    // as the card itself (server-canonical display_name first — a rename
    // PATCHes prospect_name, never title, so bare title would sort stale).
    // First vs last name is deliberately NOT distinguished (founder ruling).
    return [...items].sort((a, b) => {
      const byName = rowName(a).localeCompare(rowName(b), undefined, { sensitivity: 'base' });
      return byName !== 0 ? byName : cmpCreatedDesc(a, b);
    });
  }
  if (mode === 'category') {
    // Field pack v1: group by the card's category (a board's configured
    // field, e.g. "Role") — A→Z, uncategorized sink to the bottom,
    // created-desc tiebreak. Offered only on boards with a configured
    // category field; a stale pref elsewhere degrades to created order
    // (every category null → pure tiebreak).
    return [...items].sort((a, b) => {
      if (a.category === null && b.category === null) return cmpCreatedDesc(a, b);
      if (a.category === null) return 1;
      if (b.category === null) return -1;
      const byCategory = a.category.localeCompare(b.category, undefined, { sensitivity: 'base' });
      return byCategory !== 0 ? byCategory : cmpCreatedDesc(a, b);
    });
  }
  if (mode === 'manual' && manualIds && manualIds.length > 0) {
    const rank = new Map(manualIds.map((id, i) => [id, i]));
    const known = items.filter((i) => rank.has(i.id)).sort((a, b) => rank.get(a.id)! - rank.get(b.id)!);
    const unknown = items.filter((i) => !rank.has(i.id)); // new arrivals append (incoming order)
    return [...known, ...unknown];
  }
  // 'created' (or manual with nothing saved yet): newest first — the
  // server's board order, re-derived locally so the mode is deterministic.
  return [...items].sort(cmpCreatedDesc);
}

function cmpCreatedDesc(a: WorkItemRow, b: WorkItemRow): number {
  return b.created_at.localeCompare(a.created_at);
}

/** The card's display name for sorting — mirrors WorkItemCard's resolution
 *  (display_name ?? cardName) without importing component code into a lib. */
function rowName(r: WorkItemRow): string {
  return r.display_name ?? (r.title || r.prospect_name || '');
}

/**
 * The new manual order after dragging `activeId` onto `overId` within one
 * lane. `displayedIds` is the lane AS CURRENTLY RENDERED (whatever mode was
 * active), so switching a due-sorted lane to manual-by-dragging keeps what
 * the user was looking at. Returns null for a no-op (unknown ids / same slot).
 */
export function applyManualReorder(
  displayedIds: string[],
  activeId: string,
  overId: string,
): string[] | null {
  const from = displayedIds.indexOf(activeId);
  const to = displayedIds.indexOf(overId);
  if (from === -1 || to === -1 || from === to) return null;
  const next = [...displayedIds];
  next.splice(from, 1);
  next.splice(to, 0, activeId);
  return next;
}
